import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";

const prefersReducedMotion = () => {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
};

/**
 * The recommendation card list plus prev/next arrows for when it becomes a
 * swipe row (≤1240px, see DiscoveryPanel.css). Without a peek of the third
 * card, the arrows are what tells a mouse user there's more to see; each is
 * only rendered while there is more to scroll in its direction, and they're
 * hidden by CSS on desktop (a static grid) and on mobile (where the peeking
 * next card is the swipe cue).
 *
 * The arrows are named buttons (so voice-control users can say "click Show
 * next recommendations") but kept out of the Tab order: every card is
 * already a tab stop, and focusing one scrolls it into view.
 */
const DiscoveryCardRow = ({ label, children }) => {
  const rowRef = useRef(null);
  const listId = useId();
  const [edges, setEdges] = useState({ prev: false, next: false });

  const updateEdges = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const max = row.scrollWidth - row.clientWidth;
    const prev = row.scrollLeft > 1;
    const next = max > 1 && row.scrollLeft < max - 1;
    setEdges((cur) => (cur.prev === prev && cur.next === next ? cur : { prev, next }));
  }, []);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    updateEdges();
    row.addEventListener("scroll", updateEdges, { passive: true });
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateEdges);
    resize?.observe(row);
    return () => {
      row.removeEventListener("scroll", updateEdges);
      resize?.disconnect();
    };
  }, [updateEdges]);

  // One card per click; mandatory snapping lands it exactly on a card edge.
  const scrollByCard = (direction) => {
    const row = rowRef.current;
    if (!row) return;
    const [first, second] = row.children;
    const step = first && second ? second.offsetLeft - first.offsetLeft : row.clientWidth;
    row.scrollBy({ left: direction * step, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  return (
    <div className="discovery-cards-wrap">
      <ul ref={rowRef} id={listId} className="discovery-cards" aria-label={label}>
        {children}
      </ul>
      {edges.prev && (
        <button
          type="button"
          className="discovery-scroll discovery-scroll--prev"
          aria-label="Show previous recommendations"
          aria-controls={listId}
          tabIndex={-1}
          onClick={() => scrollByCard(-1)}
        >
          <AiOutlineLeft aria-hidden="true" />
        </button>
      )}
      {edges.next && (
        <button
          type="button"
          className="discovery-scroll discovery-scroll--next"
          aria-label="Show next recommendations"
          aria-controls={listId}
          tabIndex={-1}
          onClick={() => scrollByCard(1)}
        >
          <AiOutlineRight aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default DiscoveryCardRow;
